import React, { useEffect, useState } from 'react';
import {
    Box, Card, Typography, Table, TableHead, TableRow, TableCell,
    TableBody, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Select, MenuItem, FormControl, InputLabel, TablePagination, Stack
} from '@mui/material';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, addDoc, Timestamp, doc, deleteDoc, updateDoc, orderBy } from 'firebase/firestore';
import Shopheader from '../Shop/Shopheader';
import Mashalheader from '../Mashal/Mashalheader';
import Wholesalerheader from '../Wholesale/Wholesaleheader';
import { useParams } from "react-router-dom";
import ReplyIcon from '@mui/icons-material/Reply';
import VisibilityIcon from '@mui/icons-material/Visibility';



export default function CustomerAccountsSummary() {
    const { category } = useParams(); // dynamic value from URL

    const [Customers, setCustomers] = useState([]);
    const [CustomerAccounts, setCustomerAccounts] = useState({});
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [payType, setPayType] = useState('Cash');

    const [openPayModal, setOpenPayModal] = useState(false);
    const [openHistoryModal, setOpenHistoryModal] = useState(false);
    const [CustomerPayments, setCustomerPayments] = useState([]);
    const [editPayment, setEditPayment] = useState(null);
    const [searchText, setSearchText] = useState('');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);


    useEffect(() => {
        fetchCustomersAndAccounts();
    }, [category]);

    const fetchCustomersAndAccounts = async () => {
        try {
            const q = query(collection(db, 'customers'), where('category', '==', category));
            onSnapshot(q, (snap) => {
                const CustomerData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCustomers(CustomerData);

                CustomerData.forEach(Customer => {
                    const SalesQuery = query(collection(db, 'sales'), where('customerId', '==', Customer.id));
                    onSnapshot(SalesQuery, (snapSales) => {
                        let grand = 0;
                        snapSales.docs.forEach(p => {
                            grand += parseFloat(p.data().grandTotal || 0);
                        });

                        const paymentsQuery = query(
                            collection(db, 'customerpaymenthistory'),
                            where('customerId', '==', Customer.id)
                        );
                        onSnapshot(paymentsQuery, (snapPayments) => {
                            let paid = snapPayments.docs.reduce((acc, doc) => acc + parseFloat(doc.data().amount || 0), 0);
                            const remaining = grand - paid;
                            setCustomerAccounts(prev => ({
                                ...prev,
                                [Customer.id]: {
                                    grandTotal: grand.toFixed(2),
                                    paidTotal: paid.toFixed(2),
                                    remainingTotal: remaining.toFixed(2),
                                },
                            }));
                        });
                    });
                });
            });
        } catch (error) {
            console.error(error.message);
        }
    };

    const handlePayAmountClick = (Customer) => {
        setSelectedCustomer(Customer);
        setPaymentAmount('');
        setPaymentDate(new Date().toISOString().split('T')[0]);
        setPayType('Cash');
        setEditPayment(null);
        setOpenPayModal(true);
    };

    const handlePayAmountSave = async () => {
        if (!paymentAmount || isNaN(paymentAmount)) return;

        try {
            if (editPayment) {
                // Update existing payment
                const paymentRef = doc(db, 'customerpaymenthistory', editPayment.id);
                await updateDoc(paymentRef, {
                    amount: parseFloat(paymentAmount),
                    paytype: payType,
                    paymentDate: paymentDate,
                    updated: Timestamp.now(),
                });
            } else {
                // Add new payment
                await addDoc(collection(db, 'customerpaymenthistory'), {
                    customerId: selectedCustomer.id,
                    amount: parseFloat(paymentAmount),
                    paytype: payType,
                    paymentDate: paymentDate,
                    created: Timestamp.now(),
                });
            }

            // Refresh totals
            const paymentsQuery = query(collection(db, 'customerpaymenthistory'), where('customerId', '==', selectedCustomer.id));
            onSnapshot(paymentsQuery, (snap) => {
                const totalPaid = snap.docs.reduce((acc, doc) => acc + parseFloat(doc.data().amount || 0), 0);
                const grandTotal = parseFloat(CustomerAccounts[selectedCustomer.id]?.grandTotal || 0);
                const newRemaining = (grandTotal - totalPaid).toFixed(2);

                setCustomerAccounts(prev => ({
                    ...prev,
                    [selectedCustomer.id]: {
                        ...prev[selectedCustomer.id],
                        paidTotal: totalPaid.toFixed(2),
                        remainingTotal: newRemaining,
                    },
                }));
            });

            setOpenPayModal(false);
            setEditPayment(null);
        } catch (error) {
            console.error(error.message);
        }
    };

    const handleViewHistoryClick = (Customer) => {
        setSelectedCustomer(Customer);

        const paymentsQuery = query(
            collection(db, 'customerpaymenthistory'),
            where('customerId', '==', Customer.id),
            orderBy('paymentDate', 'desc') // <-- descending order
        );
        onSnapshot(paymentsQuery, (snap) => {
            const paymentsData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCustomerPayments(paymentsData);
        });
        setOpenHistoryModal(true);
    };

    const handleEditPayment = (payment) => {
        setEditPayment(payment);
        setPaymentAmount(payment.amount);
        setPaymentDate(payment.paymentDate);
        setPayType(payment.paytype);
        setOpenPayModal(true);
    };

    const handleDeletePayment = async (payment) => {
        if (window.confirm('Are you sure you want to delete this payment?')) {
            await deleteDoc(doc(db, 'customerpaymenthistory', payment.id));
        }
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredCustomers = Customers.filter(v =>
        v.name.toLowerCase().includes(searchText.toLowerCase()) ||
        v.mobile.includes(searchText)
    );

    return (
        <Box p={2}>
            {category === "shop" && <Shopheader />}
            {category === "mashal" && <Mashalheader />}
            {category === "wholesaler" && <Wholesalerheader />}

            <Typography variant="h5" fontWeight="bold" mb={3}>
                Customer Accounts ({category})
            </Typography>

            <TextField
                placeholder="Search by name or mobile"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size='small'
                sx={{ mb: 2 }}
            />

            <Card sx={{ p: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Actions</TableCell>
                            <TableCell>ग्राहकाचे नाव </TableCell>
                            <TableCell>मोबाइल</TableCell>
                            <TableCell>टोटल खरेदी </TableCell>
                            <TableCell>टोटल जमा </TableCell>
                            <TableCell>टोटल शिल्लक </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(Customer => (
                            <TableRow key={Customer.id}>
                                <TableCell>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<ReplyIcon />}
                                            onClick={() => handlePayAmountClick(Customer)}
                                        >
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => handleViewHistoryClick(Customer)}
                                        >
                                        </Button>
                                    </Stack>
                                </TableCell>
                                <TableCell>{Customer.name}</TableCell>
                                <TableCell>{Customer.mobile}</TableCell>
                                <TableCell>{CustomerAccounts[Customer.id]?.grandTotal || 0}</TableCell>
                                <TableCell>{CustomerAccounts[Customer.id]?.paidTotal || 0}</TableCell>
                                <TableCell>{CustomerAccounts[Customer.id]?.remainingTotal || 0}</TableCell>
                                

                            </TableRow>
                        ))}
                        {filteredCustomers.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">No Customers found.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    component="div"
                    count={filteredCustomers.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Card>

            {/* PAY AMOUNT MODAL */}
            <Dialog
                open={openPayModal} onClose={() => setOpenPayModal(false)}
                fullWidth
                
            >
                <DialogTitle>{editPayment ? 'Edit रक्कम ' : 'Pay रक्कम '} - {selectedCustomer?.name}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="रक्कम "
                        fullWidth
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        type="number"
                        margin="dense"
                        size='small'
                    />
                    <TextField
                        label="जमा दिनांक "
                        type="date"
                        fullWidth
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        margin="dense"
                        size='small'
                        InputLabelProps={{ shrink: true }}
                    />
                    <FormControl fullWidth margin="dense">
                        <InputLabel>पेमेंट टाईप </InputLabel>
                        <Select value={payType} size='small' onChange={(e) => setPayType(e.target.value)}>
                            <MenuItem value="Cash">Cash</MenuItem>
                            <MenuItem value="Online Pay">Online Pay</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography mt={1}>
                        Remaining Before Payment: {selectedCustomer ? CustomerAccounts[selectedCustomer.id]?.remainingTotal : 0}
                    </Typography>
                </DialogContent>
                <DialogActions >
                    <Button onClick={() => { setOpenPayModal(false); setEditPayment(null); }}>Cancel</Button>
                    <Button variant="contained" onClick={handlePayAmountSave}>{editPayment ? 'Update' : 'Pay'}</Button>
                </DialogActions>
            </Dialog>

            {/* PAYMENT HISTORY MODAL */}
            <Dialog
                open={openHistoryModal}
                onClose={() => setOpenHistoryModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Payment History - {selectedCustomer?.name}</DialogTitle>
                <DialogContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>जमा दिनांक</TableCell>
                                <TableCell>रक्कम</TableCell>
                                <TableCell>पेमेंट टाईप </TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {CustomerPayments.map(p => (
                                <TableRow key={p.id}>
                                    <TableCell>{p.paymentDate}</TableCell>
                                    <TableCell>{p.amount}</TableCell>
                                    <TableCell>{p.paytype}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" variant="outlined" onClick={() => handleEditPayment(p)}>Edit</Button>
                                            <Button size="small" variant="contained" color="error" onClick={() => handleDeletePayment(p)}>Delete</Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {CustomerPayments.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">No payments found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions >
                    <Button onClick={() => setOpenHistoryModal(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
}
